import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { MissingValue } from "@/profitability/missing-value";
import { rate } from "@/profitability/review/labels";
import { useReviewSpec } from "@/profitability/review/review-spec";
import type { ReportRow } from "@/profitability/types";
import type { RecordObject, RecordRef } from "./objects";
import { flagColumn, recordId, relatedRows, rowMargin } from "./objects";
import { RecordChip } from "./record-chip";
import { SectionHeading } from "./section-heading";

/**
 * The records of another object that link to this one, a technician's jobs
 * say: each by name, its margin, and a mark where it is flagged.
 */
export const RecordRelated = ({
  object,
  target,
  targetRow,
  onOpenRecord,
  limit,
  onViewAll,
}: {
  object: RecordObject;
  target: RecordObject;
  /** The record whose links these are, a row of `target`. */
  targetRow: ReportRow;
  onOpenRecord: (ref: RecordRef) => void;
  /** Show the first few, with a way to all of them. */
  limit?: number;
  onViewAll?: () => void;
}) => {
  const spec = useReviewSpec();
  const rows = relatedRows(object, target, targetRow);
  const [all, setAll] = useState(false);
  if (!rows.length) return null;
  const flag = flagColumn(object.dataset);
  const listed = limit && !all ? rows.slice(0, limit) : rows;
  // A kind of record with no rate (employees) shows names, not a column of dashes.
  const rated = rows.some((row) => rowMargin(object.dataset, row) !== null);
  return (
    <section aria-label={object.plural} className="flex flex-col gap-2">
      <SectionHeading
        count={rows.length}
        action={
          limit && rows.length > limit ? (
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0"
              onClick={() => (onViewAll ? onViewAll() : setAll(!all))}
            >
              {all ? "Show fewer" : "Show all"}
            </Button>
          ) : undefined
        }
      >
        {object.plural}
      </SectionHeading>
      <ul className="flex flex-col">
        {listed.map((row) => {
          const key = recordId(object.dataset, row);
          const margin = rowMargin(object.dataset, row);
          const flagged = !!flag && !!row[flag.key];
          return (
            <li key={key}>
              <button
                type="button"
                onClick={() =>
                  onOpenRecord({ dataset: object.dataset.id, recordId: key })
                }
                className="-mx-1.5 flex h-8 w-[calc(100%+0.75rem)] items-center justify-between gap-3 rounded-md px-1.5 text-start text-sm transition-colors duration-150 hover:bg-neutral-subtle-hovered focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
              >
                <span className="flex min-w-0 items-center gap-1.5">
                  <RecordChip
                    object={object}
                    name={String(row[object.nameColumn.key])}
                    variant="plain"
                  />
                  {flagged && (
                    <WarningIcon
                      aria-label="Flagged"
                      className="size-3.5 flex-none text-warning"
                    />
                  )}
                </span>
                {rated && (
                  <span className="flex-none text-subtle tabular-nums">
                    {margin === null ? <MissingValue /> : rate(margin)}
                    <span className="sr-only"> {spec.metric.label}</span>
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
};
